import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MagnifyingGlass, Pencil, FloppyDisk, X, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditClient = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [allClients, setAllClients] = useState([]); // All client list clients
  const [filteredClients, setFilteredClients] = useState([]); // Filtered by search
  const [loading, setLoading] = useState(true);
  const [editingClient, setEditingClient] = useState(null);
  const [editForm, setEditForm] = useState({
    username: '',
    phone: '',
    password: ''
  });
  const [saving, setSaving] = useState(false);

  // Load all client list clients on mount
  useEffect(() => {
    fetchAllClients();
  }, []);

  // Filter clients when search query changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredClients(allClients);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = allClients.filter(client =>
        client.username?.toLowerCase().includes(query) ||
        client.phone?.includes(query)
      );
      setFilteredClients(filtered);
    }
  }, [searchQuery, allClients]);

  const fetchAllClients = async () => {
    try {
      setLoading(true);
      // Get all registered customers
      const response = await axios.get(`${API}/clients/registered`);
      setAllClients(response.data);
      setFilteredClients(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load clients:', error);
      toast.error('Failed to load clients');
      setLoading(false);
    }
  };

  const startEditing = (client) => {
    setEditingClient(client);
    setEditForm({
      username: client.username,
      phone: client.phone,
      password: ''  // Empty by default
    });
  };

  const cancelEditing = () => {
    setEditingClient(null);
    setEditForm({ username: '', phone: '', password: '' });
  };

  const handleSave = async () => {
    if (!editForm.username || !editForm.phone) {
      toast.error('Username and phone are required');
      return;
    }

    if (editForm.phone.length !== 10) {
      toast.error('Phone must be 10 digits');
      return;
    }

    if (editForm.password && editForm.password.length < 5) {
      toast.error('Password must be at least 5 characters');
      return;
    }

    try {
      setSaving(true);
      
      const updateData = {
        username: editForm.username,
        phone: editForm.phone
      };

      // Only include password if it's being changed
      if (editForm.password && editForm.password.trim()) {
        updateData.password = editForm.password;
      }

      await axios.put(`${API}/admin/clients/${editingClient.id}/update`, updateData);
      
      toast.success('✅ Client details updated successfully!');
      
      // Refresh client list to show updated data
      await fetchAllClients();
      
      cancelEditing();
      setSaving(false);
    } catch (error) {
      console.error('Update failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to update client');
      setSaving(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="h-screen flex flex-col bg-wa-hover">
      {/* Header */}
      <div className="bg-wa-primary-strong text-white p-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin-settings')}
            className="p-2 hover:bg-wa-primary-strong rounded-full"
          >
            <ArrowLeft size={24} weight="bold" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Edit Client's Login Details</h1>
            <p className="text-sm text-green-100">Search and update client credentials</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-4xl mx-auto">
          {/* Search Bar with Refresh */}
          <div className="bg-wa-panel rounded-lg shadow-sm p-4 mb-4">
            <div className="flex gap-2 mb-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Search by name or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-wa-border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <MagnifyingGlass
                  size={20}
                  className="absolute left-3 top-3.5 text-wa-text-secondary"
                />
              </div>
              <button
                onClick={fetchAllClients}
                disabled={loading}
                className="px-4 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 disabled:opacity-50 transition-colors flex items-center gap-2"
                title="Refresh list"
              >
                <ArrowsClockwise size={20} weight="bold" />
                Refresh
              </button>
            </div>
            <p className="text-xs text-wa-text-secondary">
              Showing {filteredClients.length} of {allClients.length} registered customers
            </p>
          </div>

          {/* Client List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-wa-text-secondary">Loading clients...</div>
            </div>
          ) : allClients.length === 0 ? (
            <div className="text-center py-12 text-wa-text-secondary">
              <p className="text-lg">No registered customers yet</p>
              <p className="text-sm mt-2">Customers will appear here automatically when they register</p>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-12 text-wa-text-secondary">
              <p className="text-lg">🔍 No clients match your search</p>
              <p className="text-sm mt-2">Try different keywords</p>
            </div>
          ) : (
            <div className="bg-wa-panel rounded-lg shadow-sm">
              <div className="p-4 border-b bg-wa-hover">
                <h2 className="font-semibold text-wa-text-primary">
                  Registered Customers ({filteredClients.length})
                </h2>
                <p className="text-xs text-wa-text-secondary mt-1">Click Edit to update credentials</p>
              </div>
              <div className="divide-y max-h-[calc(100vh-350px)] overflow-y-auto">
                {filteredClients.map((client) => (
                  <div key={client.id} className="p-4">
                    {editingClient?.id === client.id ? (
                      /* Edit Form */
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-wa-text-primary mb-1">
                            Name
                          </label>
                          <input
                            type="text"
                            value={editForm.username}
                            onChange={(e) =>
                              setEditForm({ ...editForm, username: e.target.value })
                            }
                            className="w-full px-3 py-2 border border-wa-border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-wa-text-primary mb-1">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            value={editForm.phone}
                            onChange={(e) =>
                              setEditForm({ ...editForm, phone: e.target.value })
                            }
                            maxLength={10}
                            className="w-full px-3 py-2 border border-wa-border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-wa-text-primary mb-1">
                            New Password (leave empty to keep current)
                          </label>
                          {/* Reset to Default Button */}
                          <button
                            type="button"
                            onClick={() => setEditForm({ ...editForm, password: 'Abcd1234' })}
                            className="mb-2 px-3 py-1.5 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition-colors font-medium flex items-center gap-1"
                          >
                            <ArrowsClockwise size={16} weight="bold" />
                            Reset to Default
                          </button>
                          <input
                            type="password"
                            value={editForm.password}
                            onChange={(e) =>
                              setEditForm({ ...editForm, password: e.target.value })
                            }
                            placeholder="Enter new password or leave empty"
                            className="w-full px-3 py-2 border border-wa-border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                          <p className="text-xs text-wa-text-secondary mt-1">
                            Default password: <span className="font-mono font-semibold">Abcd1234</span>
                          </p>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={handleSave}
                            disabled={saving}
                            className="flex-1 bg-wa-primary-strong text-white py-2 rounded-lg font-semibold hover:bg-wa-primary-strong disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            <FloppyDisk size={20} weight="bold" />
                            {saving ? 'Saving...' : 'Save Changes'}
                          </button>
                          <button
                            onClick={cancelEditing}
                            disabled={saving}
                            className="flex-1 bg-gray-200 text-wa-text-primary py-2 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            <X size={20} weight="bold" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Client Display */
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-wa-primary-strong flex items-center justify-center text-white font-semibold text-lg">
                            {client.profile_pic ? (
                              <img
                                src={client.profile_pic.startsWith('http') ? client.profile_pic : `${BACKEND_URL}${client.profile_pic}`}
                                alt={client.username}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              client.username?.charAt(0)?.toUpperCase() || 'U'
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-wa-text-primary">
                              {client.username}
                            </h3>
                            <p className="text-sm text-wa-text-secondary">{client.phone}</p>
                            <p className="text-xs text-wa-text-secondary mt-1">
                              Registered: {formatDate(client.created_at)}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => startEditing(client)}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 transition-colors"
                        >
                          <Pencil size={18} weight="bold" />
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditClient;
